import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, fmtErr, API } from "@/lib/api";
import FileDropzone from "@/components/FileDropzone";
import BrandMark from "@/components/BrandMark";
import { toast } from "sonner";
import {
  Calendar, Send, FileSpreadsheet, Type, MessageSquare, ChevronRight, Loader2,
  MessageCircle, Sparkles, CheckCircle2, Phone, AlignLeft, X, AlertTriangle,
  Eye, Clock, Users, Download, FileDown, LogOut,
} from "lucide-react";

const MODE_TABS = [
  { id: "manual", label: "Manuel Liste", icon: Type, desc: "Telefonları yapıştır + tek mesaj" },
  { id: "file_message", label: "Dosya · Telefon + Mesaj", icon: FileSpreadsheet, desc: "Her satır kendi mesajıyla (N×N)" },
  { id: "file_template", label: "Dosya · Şablon", icon: MessageSquare, desc: "Parametreli kişiselleştirilmiş mesaj" },
];

/** Her satır/virgül parçasından sadece rakamları çıkarır (API'ye temiz gönderim). */
function formatManualPhonesForApi(text) {
  if (!text?.trim()) return "";
  return text
    .replace(/\r\n/g, "\n")
    .replace(/\r/g, "\n")
    .split("\n")
    .flatMap((line) => line.split(/[,;]+/))
    .map((chunk) => chunk.replace(/\D/g, ""))
    .filter((digits) => digits.length > 0)
    .join("\n");
}

export default function CustomerPortal() {
  const params = useParams();
  const nav = useNavigate();
  // Token comes from URL (legacy /sms/:token) OR from localStorage (after login)
  const stored = typeof window !== "undefined" ? localStorage.getItem("sms_portal_token") : null;
  const token = params.token || stored;
  const loggedIn = !params.token && !!stored;   // arrived via /portal after login
  const portalUser = typeof window !== "undefined" ? localStorage.getItem("sms_portal_user") || "" : "";

  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  const [mode, setMode] = useState("manual");
  const [scheduleAt, setScheduleAt] = useState("");
  const [selectedOriginator, setSelectedOriginator] = useState("");

  // manual mode
  const [phonesText, setPhonesText] = useState("");
  const [message, setMessage] = useState("");

  // file modes
  const [parsing, setParsing] = useState(false);
  const [fileData, setFileData] = useState(null);
  const [phoneCol, setPhoneCol] = useState("");
  const [messageCol, setMessageCol] = useState("");
  const [template, setTemplate] = useState("");

  // preview & send flow
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [job, setJob] = useState(null);
  const jobRef = useRef(null);
  const formResetForJobRef = useRef(null);
  const [dropzoneKey, setDropzoneKey] = useState(0);

  const isLocked = !!job && !["completed", "failed", "partial"].includes(job.status);

  const resetForm = () => {
    setPreview(null);
    setPhonesText("");
    setMessage("");
    setFileData(null);
    setPhoneCol("");
    setMessageCol("");
    setTemplate("");
    setScheduleAt("");
    setDropzoneKey((k) => k + 1);
    if (info?.originators?.length) setSelectedOriginator(info.originators[0]);
  };

  const newSend = () => {
    setJob(null);
    formResetForJobRef.current = null;
    resetForm();
  };

  const logout = () => {
    localStorage.removeItem("sms_portal_token");
    localStorage.removeItem("sms_portal_user");
    localStorage.removeItem("sms_portal_customer");
    nav("/admin/login");
  };

  useEffect(() => {
    // No token at all → user landed on /portal without login → send to login
    if (!token) {
      nav("/admin/login");
      return;
    }
    api.get(`/portal/${token}/info`).then((r) => {
      setInfo(r.data);
      const origs = r.data.originators || (r.data.originator ? [r.data.originator] : []);
      if (origs.length) setSelectedOriginator(origs[0]);
    }).catch((e) => {
      // Invalid stored token → kick back to login
      if (loggedIn) {
        localStorage.removeItem("sms_portal_token");
        nav("/admin/login");
        return;
      }
      setErr(fmtErr(e?.response?.data?.detail) || "Bağlantı geçersiz");
    });
  }, [token]);

  useEffect(() => {
    if (!job || ["completed", "failed", "partial"].includes(job.status)) return;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get(`/portal/${token}/jobs/${job.job_id || job.id}`);
        setJob({ ...data, job_id: data.id });
      } catch { /* ignore */ }
    }, 1500);
    return () => clearInterval(t);
  }, [job, token]);

  useEffect(() => {
    if (!job) return;
    const terminal = ["completed", "failed", "partial"];
    if (!terminal.includes(job.status)) return;
    const jobId = job.job_id || job.id;
    if (!jobId || formResetForJobRef.current === jobId) return;
    formResetForJobRef.current = jobId;
    resetForm();
  }, [job, info]);

  const clearFile = () => { setFileData(null); setPhoneCol(""); setMessageCol(""); };

  const onFile = async (f) => {
    setParsing(true);
    setFileData(null); setPhoneCol(""); setMessageCol("");
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post(`/portal/${token}/parse-file`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setFileData(data);
      const phoneKeys = ["telefon", "phone", "gsm", "tel", "numara", "no", "cep"];
      const msgKeys = ["mesaj", "message", "metin", "icerik", "içerik", "sms"];
      const suggestPhone = data.columns.find((c) => phoneKeys.includes(String(c).toLowerCase().trim()));
      const suggestMsg = data.columns.find((c) => msgKeys.includes(String(c).toLowerCase().trim()));
      if (suggestPhone) setPhoneCol(suggestPhone);
      if (suggestMsg) setMessageCol(suggestMsg);
      toast.success(`${data.total} satır okundu · ${data.columns.length} sütun`);
    } catch (e) {
      toast.error(fmtErr(e?.response?.data?.detail) || "Dosya okunamadı");
    } finally {
      setParsing(false);
    }
  };

  const buildPayload = (extra = {}) => {
    const payload = { mode, schedule_at: scheduleAt || null, originator: selectedOriginator || undefined, ...extra };
    if (mode === "manual") {
      payload.phones = formatManualPhonesForApi(phonesText);
      payload.message = message;
    } else if (mode === "file_message") {
      payload.rows = fileData?.rows || [];
      payload.phone_column = phoneCol;
      payload.message_column = messageCol;
    } else if (mode === "file_template") {
      payload.template = template;
      payload.rows = fileData?.rows || [];
      payload.phone_column = phoneCol;
    }
    return payload;
  };

  const onPreview = async (e) => {
    e?.preventDefault();
    if ((mode === "file_message" || mode === "file_template") && !phoneCol) {
      toast.error("Telefon sütununu seçin");
      return;
    }
    if (mode === "file_message" && !messageCol) {
      toast.error("Mesaj sütununu seçin");
      return;
    }
    setPreviewing(true);
    try {
      const { data } = await api.post(`/portal/${token}/preview-send`, buildPayload());
      if (data.total === 0) {
        toast.error("Gönderilecek geçerli numara bulunamadı");
        setPreview(null);
        return;
      }
      setPreview(data);
    } catch (e) {
      toast.error(fmtErr(e?.response?.data?.detail) || "Önizleme alınamadı");
    } finally {
      setPreviewing(false);
    }
  };

  const onConfirmSend = async (confirmMulti = false) => {
    setSubmitting(true);
    try {
      const { data } = await api.post(`/portal/${token}/send`, buildPayload({ confirm_multi_message: confirmMulti }));
      toast.success(`İş kuyruğa alındı · ${data.total} alıcı`);
      setJob({ ...data, status: "queued" });
      setPreview(null);
      // Scroll to job progress card after a tiny delay (let DOM render)
      setTimeout(() => {
        jobRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 150);
    } catch (e) {
      toast.error(fmtErr(e?.response?.data?.detail) || "Gönderim başarısız");
    } finally {
      setSubmitting(false);
    }
  };

  const insertPlaceholder = (col) => setTemplate((t) => `${t}{${col}}`);

  if (err) return <PortalShell loggedIn={loggedIn} onLogout={logout} portalUser={portalUser}><div className="card-soft p-8 text-red-700 font-semibold">{err}</div></PortalShell>;
  if (!info) return <PortalShell loggedIn={loggedIn} onLogout={logout} portalUser={portalUser}><div className="text-slate-500">Yükleniyor…</div></PortalShell>;

  const charCount = (mode === "manual" ? message : (mode === "file_template" ? template : "")).length;
  const smsCount = charCount > 0 ? Math.ceil(charCount / 160) : 0;

  return (
    <PortalShell info={info} loggedIn={loggedIn} onLogout={logout} portalUser={portalUser}>
      {/* Mode tabs */}
      {info?.test_mode && (
        <div className="mb-4 p-3 bg-amber-50 border border-amber-300 rounded-xl flex items-center gap-2 text-amber-900 text-sm" data-testid="test-mode-banner">
          <AlertTriangle className="w-4 h-4 text-amber-700 shrink-0" />
          <span><strong>TEST MODU AÇIK</strong> — SMS&apos;ler gerçekten gönderilmeyecek, kredi düşmez. (Admin kapatana kadar geçerli)</span>
        </div>
      )}
      <div className={`card-soft mb-6 overflow-hidden p-1.5 ${isLocked ? "opacity-60 pointer-events-none" : ""}`} data-testid="mode-tabs">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-1.5">
          {MODE_TABS.map((t) => {
            const active = mode === t.id;
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                type="button"
                disabled={isLocked}
                onClick={() => { setMode(t.id); setJob(null); setPreview(null); }}
                data-testid={`mode-${t.id}`}
                className={`text-left p-4 rounded-lg transition-all ${active
                  ? "bg-gradient-to-br from-turq-600 to-cyan-500 text-white shadow-md shadow-turq-500/30"
                  : "hover:bg-turq-50 text-slate-700"}`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <Icon className="w-4 h-4" />
                  <div className="text-[11px] font-bold uppercase tracking-wider">{t.label}</div>
                </div>
                <div className={`text-xs ${active ? "text-white/85" : "text-slate-500"}`}>{t.desc}</div>
              </button>
            );
          })}
        </div>
      </div>

      <form onSubmit={onPreview} className={`card-soft p-6 sm:p-10 space-y-12 ${isLocked ? "opacity-60" : ""}`}>
        <fieldset disabled={isLocked} className="contents">
        {mode === "manual" && (
          <>
            <Field label="Telefon Numaraları" hint="Her satır veya virgülle ayrılmış bir numara. Boşluk, tire, parantez otomatik temizlenir. 5XX… / 05XX… / +90 5XX…">
              <textarea
                rows={8} value={phonesText} onChange={(e) => setPhonesText(e.target.value)}
                placeholder={"5321234567\n05451112233\n+905339998877"}
                data-testid="manual-phones" className="input-soft font-mono"
              />
            </Field>
            <Field label="Mesaj" hint={`${charCount} karakter · ${smsCount || 1} SMS`}>
              <textarea
                rows={5} value={message} onChange={(e) => setMessage(e.target.value)}
                placeholder="Mesajınızı yazın..." data-testid="manual-message" className="input-soft"
              />
            </Field>
          </>
        )}

        {(mode === "file_message" || mode === "file_template") && (
          <>
            <div className="rounded-xl bg-gradient-to-r from-turq-50 to-cyan-50 border border-turq-200 p-4">
              <div className="flex items-start justify-between flex-wrap gap-3">
                <div className="flex items-start gap-3 min-w-0">
                  <div className="w-9 h-9 rounded-lg bg-white border border-turq-200 flex items-center justify-center shrink-0">
                    <FileDown className="w-4 h-4 text-turq-700" />
                  </div>
                  <div className="min-w-0">
                    <div className="font-semibold text-sm text-slate-900">Örnek Şablon İndir</div>
                    <div className="text-xs text-slate-600 mt-0.5">
                      {mode === "file_message"
                        ? "İki sütun: Telefon + Mesaj. Her satırın kendi mesajı gönderilir."
                        : "Telefon sütunu + istediğiniz parametreler (örn. Ad, Borc). Mesaj şablonunda {Ad} {Borc} olarak kullanırsınız."}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <a
                    href={`${API}/portal/${token}/sample-template?mode=${mode}&format=xlsx`}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`download-xlsx-${mode}`}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-turq-300 text-turq-800 rounded-lg text-xs font-semibold hover:bg-turq-50"
                  >
                    <Download className="w-3.5 h-3.5" /> Excel (XLSX)
                  </a>
                  <a
                    href={`${API}/portal/${token}/sample-template?mode=${mode}&format=csv`}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`download-csv-${mode}`}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-turq-300 text-turq-800 rounded-lg text-xs font-semibold hover:bg-turq-50"
                  >
                    <Download className="w-3.5 h-3.5" /> CSV
                  </a>
                </div>
              </div>
            </div>

            <Field label="1. Dosya Yükle" hint={mode === "file_template" ? "Telefonları içeren bir Excel/CSV/TXT yükleyin." : "Telefon ve mesaj içeren bir Excel/CSV/TXT yükleyin."}>
              <FileDropzone key={dropzoneKey} onFile={onFile} testid={`dropzone-${mode}`} onClear={clearFile} />
              {parsing && <div className="mt-3 text-sm text-slate-600 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin text-turq-600" /> Dosya okunuyor…</div>}
            </Field>

            {fileData && (
              <>
                <div className="bg-turq-50/60 border border-turq-100 rounded-xl p-5 !mt-14" data-testid="column-mapping">
                  <div className="flex items-center gap-2 mb-4">
                    <Sparkles className="w-4 h-4 text-turq-600" />
                    <div className="font-display font-bold text-base text-slate-900">2. Sütunları Eşle</div>
                    <span className="ml-auto text-[11px] text-slate-500">{fileData.total.toLocaleString("tr-TR")} satır · {fileData.columns.length} sütun</span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="tiny-label flex items-center gap-1.5 mb-2"><Phone className="w-3 h-3" /> Telefon Sütunu *</label>
                      <select value={phoneCol} onChange={(e) => setPhoneCol(e.target.value)} data-testid="phone-col-select" className="input-soft">
                        <option value="">— Sütun seçin —</option>
                        {fileData.columns.map((c) => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                    {mode === "file_message" && (
                      <div>
                        <label className="tiny-label flex items-center gap-1.5 mb-2"><AlignLeft className="w-3 h-3" /> Mesaj Sütunu *</label>
                        <select value={messageCol} onChange={(e) => setMessageCol(e.target.value)} data-testid="message-col-select" className="input-soft">
                          <option value="">— Sütun seçin —</option>
                          {fileData.columns.map((c) => <option key={c} value={c}>{c}</option>)}
                        </select>
                      </div>
                    )}
                  </div>
                  {phoneCol && (
                    <div className="mt-3 text-xs text-turq-700 flex items-center gap-1.5">
                      <CheckCircle2 className="w-3.5 h-3.5" />
                      Telefon: <code className="font-mono font-semibold">{phoneCol}</code>
                      {mode === "file_message" && messageCol && <>&nbsp;·&nbsp;Mesaj: <code className="font-mono font-semibold">{messageCol}</code></>}
                    </div>
                  )}
                </div>

                <div className="card-soft overflow-hidden mt-10" data-testid="file-preview">
                  <div className="px-5 py-3 bg-slate-50/50 border-b border-turq-100">
                    <div className="font-semibold text-sm text-slate-900">Önizleme (ilk 10 satır)</div>
                  </div>
                  <div className="overflow-x-auto max-h-64">
                    <table className="w-full text-xs">
                      <thead className="bg-white sticky top-0">
                        <tr className="border-b border-turq-100">
                          {fileData.columns.map((c) => {
                            const isPhone = c === phoneCol;
                            const isMsg = c === messageCol;
                            return (
                              <th key={c} className={`px-3 py-2 text-left tiny-label whitespace-nowrap ${isPhone ? "bg-turq-100/60 text-turq-800" : isMsg ? "bg-cyan-100/60 text-cyan-800" : ""}`}>
                                {c}{isPhone && " · TEL"}{isMsg && " · MSJ"}
                              </th>
                            );
                          })}
                        </tr>
                      </thead>
                      <tbody>
                        {fileData.preview.map((row, i) => (
                          <tr key={i} className="border-b border-slate-100">
                            {fileData.columns.map((c) => (
                              <td key={c} className={`px-3 py-1.5 font-mono whitespace-nowrap ${c === phoneCol ? "text-turq-800 font-semibold" : "text-slate-700"}`}>
                                {String(row[c] ?? "")}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}

            {mode === "file_template" && fileData && (
              <Field label="3. Mesaj Şablonu" hint={`${charCount} karakter · ${smsCount || 1} SMS`}>
                <div className="flex flex-wrap gap-2 mb-2">
                  {fileData.columns.filter((c) => c !== phoneCol).map((c) => (
                    <button key={c} type="button" onClick={() => insertPlaceholder(c)} data-testid={`insert-${c}`} className="text-[11px] font-mono px-2.5 py-1 bg-turq-50 border border-turq-200 text-turq-800 rounded-lg hover:bg-turq-100 transition-colors">
                      + {`{${c}}`}
                    </button>
                  ))}
                </div>
                <textarea rows={5} value={template} onChange={(e) => setTemplate(e.target.value)}
                  placeholder="Sayın {Ad}, {Borc} TL borcunuz hatırlatılır."
                  data-testid="template-input" className="input-soft"
                />
                {template && fileData.preview[0] && (
                  <div className="mt-2 p-3 bg-gradient-to-br from-turq-50 to-cyan-50 border border-turq-100 rounded-lg text-xs">
                    <div className="tiny-label mb-1">Önizleme (ilk satır)</div>
                    <div className="font-mono text-slate-900">
                      {template.replace(/\{([^}]+)\}/g, (_, k) => {
                        const key = Object.keys(fileData.preview[0]).find((x) => x.toLowerCase().trim() === k.toLowerCase().trim());
                        return key ? String(fileData.preview[0][key] ?? "") : "";
                      })}
                    </div>
                  </div>
                )}
              </Field>
            )}
          </>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <Field label="Zamanlama (Opsiyonel)" hint="Boş bırakırsanız hemen gönderilir.">
            <div className="flex items-center gap-2 pb-2">
              <Calendar className="w-4 h-4 text-turq-600 shrink-0" />
              <input type="datetime-local" value={scheduleAt} onChange={(e) => setScheduleAt(e.target.value)}
                data-testid="schedule-at" className="input-soft" />
              {scheduleAt && <button type="button" onClick={() => setScheduleAt("")} className="text-xs text-slate-500 hover:text-slate-900 shrink-0">temizle</button>}
            </div>
          </Field>

          <Field label="Gönderen (Originator)" hint={info.originators?.length > 1 ? `${info.originators.length} başlık tanımlı` : "Tek başlık tanımlı"}>
            <div className="pb-2">
              {info.originators && info.originators.length > 1 ? (
                <select
                  value={selectedOriginator}
                  onChange={(e) => setSelectedOriginator(e.target.value)}
                  disabled={isLocked}
                  data-testid="originator-select"
                  className="input-soft font-mono"
                >
                  {info.originators.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              ) : (
                <div className="input-soft font-mono bg-slate-50 cursor-not-allowed">{selectedOriginator || info.originator}</div>
              )}
            </div>
          </Field>
        </div>

        <div className="flex items-center justify-end pt-5 border-t border-turq-100">
          <button
            type="submit"
            disabled={previewing || isLocked}
            data-testid="preview-btn"
            className="flex items-center gap-2 btn-primary text-sm font-semibold px-6 py-3 rounded-xl disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {previewing ? <Loader2 className="w-4 h-4 animate-spin" /> : isLocked ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
            {isLocked ? "Gönderim sürüyor…" : "Önizle ve Devam Et"}
            {!isLocked && <ChevronRight className="w-4 h-4" />}
          </button>
        </div>
        </fieldset>
      </form>

      {preview && (
        <PreviewModal
          preview={preview}
          onCancel={() => setPreview(null)}
          onConfirm={onConfirmSend}
          submitting={submitting}
        />
      )}

      {job && <JobResult job={job} jobRef={jobRef} onNew={newSend} />}
    </PortalShell>
  );
}

function Field({ label, hint, children }) {
  return (
    <div className="pt-2">
      <div className="flex items-end justify-between mb-5">
        <label className="tiny-label text-[11px]">{label}</label>
        {hint && <span className="text-[11px] text-slate-500">{hint}</span>}
      </div>
      {children}
    </div>
  );
}

function PreviewModal({ preview, onCancel, onConfirm, submitting }) {
  const {
    total, invalid, originator, scheduled_at, api_start_date, samples,
    char_count, sms_per_msg, estimated_total_sms, estimated_batches,
    duplicates_removed = 0, multi_message_phones = [],
    multi_message_phones_total = 0, needs_confirm = false,
    api_expire_date,
  } = preview;
  const schedLabel = scheduled_at ? new Date(scheduled_at).toLocaleString("tr-TR") : null;
  const [confirmedMulti, setConfirmedMulti] = useState(false);
  const blockSend = needs_confirm && !confirmedMulti;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm" data-testid="preview-modal">
      <div className="bg-white border border-turq-100 rounded-2xl w-full max-w-2xl shadow-2xl max-h-[92vh] overflow-y-auto">
        <div className="p-6 border-b border-turq-100 sticky top-0 bg-white/90 backdrop-blur z-10 flex items-start justify-between">
          <div>
            <div className="inline-flex items-center gap-1.5 mb-2">
              <Eye className="w-3.5 h-3.5 text-turq-600" />
              <div className="tiny-label">Gönderim Önizlemesi</div>
            </div>
            <h3 className="font-display font-black text-2xl text-slate-900">Göndermek üzeresiniz</h3>
            <p className="text-sm text-slate-600 mt-1">Aşağıdaki bilgileri kontrol edin. Onaylarsanız SMS&apos;ler arka planda gönderilmeye başlanacak.</p>
          </div>
          <button onClick={onCancel} disabled={submitting} className="p-2 hover:bg-turq-50 rounded-lg text-slate-500"><X className="w-4 h-4" /></button>
        </div>

        <div className="p-6 space-y-5">
          {/* Summary stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat icon={Users} label="Geçerli Alıcı" value={total.toLocaleString("tr-TR")} accent="from-turq-600 to-cyan-500" />
            <Stat icon={AlertTriangle} label="Hatalı / Atlandı" value={(invalid || 0).toLocaleString("tr-TR")} accent="from-amber-500 to-orange-500" muted={!invalid} />
            <Stat icon={MessageSquare} label="Toplam SMS" value={estimated_total_sms.toLocaleString("tr-TR")} subtitle={`${sms_per_msg}× msg`} accent="from-turq-600 to-cyan-500" />
            <Stat icon={FileSpreadsheet} label="Paket" value={estimated_batches} subtitle="× 500 alıcı" accent="from-slate-700 to-slate-500" />
          </div>

          {/* Meta */}
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
            <div>
              <div className="tiny-label mb-1">Gönderici (Originator)</div>
              <div className="font-mono text-slate-900">{originator}</div>
            </div>
            <div>
              <div className="tiny-label mb-1 flex items-center gap-1"><Clock className="w-3 h-3" /> Gönderim Zamanı</div>
              <div className="font-mono text-slate-900">
                {schedLabel ? schedLabel : <span className="text-emerald-700">Hemen</span>}
                {api_start_date && <span className="block text-[10px] text-slate-500 mt-0.5">STARTDATE: {api_start_date}</span>}
                {api_expire_date && <span className="block text-[10px] text-slate-500">EXPIREDATE: {api_expire_date} <span className="text-slate-400">(start + 24s)</span></span>}
              </div>
            </div>
            <div>
              <div className="tiny-label mb-1">Karakter Sayısı (ilk mesaj)</div>
              <div className="font-mono text-slate-900">{char_count} karakter · {sms_per_msg} SMS</div>
            </div>
          </div>

          {/* Samples */}
          <div>
            <div className="tiny-label mb-2">Örnek Mesajlar (ilk {samples.length})</div>
            <div className="space-y-2">
              {samples.map((s, i) => (
                <div key={i} className="border border-turq-100 bg-white rounded-lg p-3 flex items-start gap-3" data-testid={`sample-${i}`}>
                  <div className="font-mono text-xs text-turq-700 bg-turq-50 px-2 py-1 rounded shrink-0">{s.phone}</div>
                  <div className="text-sm text-slate-800 flex-1 break-words">{s.message}</div>
                </div>
              ))}
            </div>
            {total > samples.length && (
              <div className="text-xs text-slate-500 mt-2">+ {(total - samples.length).toLocaleString("tr-TR")} alıcı daha…</div>
            )}
          </div>

          {invalid > 0 && (
            <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-900" data-testid="warn-invalid">
              <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
              <div><strong>{invalid}</strong> satır geçersiz numara nedeniyle atlandı. Sadece TR mobil (5XX…) formatındakiler gönderilecek.</div>
            </div>
          )}

          {duplicates_removed > 0 && (
            <div className="flex items-start gap-2 p-3 bg-sky-50 border border-sky-200 rounded-lg text-xs text-sky-900" data-testid="warn-duplicates">
              <CheckCircle2 className="w-4 h-4 text-sky-600 shrink-0 mt-0.5" />
              <div>
                <strong>{duplicates_removed.toLocaleString("tr-TR")}</strong> tekrarlanan kayıt otomatik temizlendi
                <span className="text-sky-700/80"> · (aynı numaraya aynı mesaj)</span>
              </div>
            </div>
          )}

          {needs_confirm && (
            <div className="p-4 bg-rose-50 border-2 border-rose-300 rounded-xl text-sm text-rose-900 space-y-3" data-testid="warn-multi-message">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-5 h-5 text-rose-600 shrink-0 mt-0.5" />
                <div>
                  <div className="font-bold text-rose-900">Dikkat: Aynı numaraya farklı mesajlar</div>
                  <div className="text-rose-800 mt-1">
                    <strong>{multi_message_phones_total.toLocaleString("tr-TR")}</strong> numara birden fazla farklı mesaj alacak.
                    Bu kasıtlı bir gönderim ise onaylayın.
                  </div>
                </div>
              </div>
              {multi_message_phones.length > 0 && (
                <div className="max-h-32 overflow-y-auto bg-white/70 border border-rose-200 rounded-lg p-2 font-mono text-[11px] text-rose-900 space-y-0.5">
                  {multi_message_phones.map((m, i) => (
                    <div key={i} className="flex justify-between gap-2">
                      <span>{m.phone}</span>
                      <span className="text-rose-600">{m.message_count} mesaj</span>
                    </div>
                  ))}
                  {multi_message_phones_total > multi_message_phones.length && (
                    <div className="text-rose-500 text-center pt-1">+ {multi_message_phones_total - multi_message_phones.length} numara daha…</div>
                  )}
                </div>
              )}
              <label className="flex items-start gap-2 cursor-pointer select-none bg-white/80 border border-rose-200 rounded-lg p-2.5">
                <input
                  type="checkbox"
                  checked={confirmedMulti}
                  onChange={(e) => setConfirmedMulti(e.target.checked)}
                  className="mt-0.5 w-4 h-4 accent-rose-600"
                  data-testid="confirm-multi-checkbox"
                />
                <span className="text-xs text-rose-900 font-medium">
                  Onaylıyorum: Aynı numaralara birden fazla farklı SMS gönderilmesini istiyorum.
                </span>
              </label>
            </div>
          )}
        </div>

        <div className="p-6 border-t border-turq-100 flex items-center justify-end gap-3 bg-white sticky bottom-0 shadow-[0_-8px_16px_-12px_rgba(15,76,71,0.15)]">
          <button onClick={onCancel} disabled={submitting} data-testid="preview-cancel" className="px-4 py-2.5 text-sm font-semibold text-slate-700 border border-slate-200 rounded-xl hover:bg-slate-50">
            İptal · Düzenle
          </button>
          <button
            onClick={() => onConfirm(confirmedMulti)}
            disabled={submitting || blockSend}
            data-testid="preview-confirm"
            className="btn-primary text-sm font-semibold px-6 py-2.5 rounded-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Onayla ve Gönder
          </button>
        </div>
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, subtitle, accent = "from-turq-600 to-cyan-500", muted = false }) {
  return (
    <div className={`p-3 rounded-xl border ${muted ? "bg-slate-50 border-slate-200" : "bg-white border-turq-100"}`}>
      <div className="flex items-center justify-between">
        <div className="tiny-label">{label}</div>
        <Icon className={`w-3.5 h-3.5 ${muted ? "text-slate-400" : "text-turq-600"}`} />
      </div>
      <div className={`font-display font-black text-2xl mt-1 ${muted ? "text-slate-400" : `bg-gradient-to-br ${accent} bg-clip-text text-transparent`}`}>{value}</div>
      {subtitle && <div className="text-[10px] text-slate-500 mt-0.5">{subtitle}</div>}
    </div>
  );
}

function JobResult({ job, jobRef, onNew }) {
  const total = job.total || 0;
  const processed = job.processed || 0;
  const pct = total > 0 ? Math.round((processed / total) * 100) : 0;
  const isDone = ["completed", "failed", "partial"].includes(job.status);
  const isFail = job.status === "failed";
  const isPartial = job.status === "partial";
  const gw = job.last_gateway_response;

  let stateColor = "from-turq-600 to-cyan-500";
  let stateBg = "bg-turq-50 border-turq-100";
  let stateLabel = "Gönderiliyor…";
  if (job.status === "completed") { stateColor = "from-emerald-500 to-teal-500"; stateBg = "bg-emerald-50 border-emerald-200"; stateLabel = "Tamamlandı"; }
  else if (job.status === "partial") { stateColor = "from-amber-500 to-orange-500"; stateBg = "bg-amber-50 border-amber-200"; stateLabel = "Kısmi Başarı"; }
  else if (job.status === "failed") { stateColor = "from-red-500 to-rose-500"; stateBg = "bg-red-50 border-red-200"; stateLabel = "Başarısız"; }
  else if (job.status === "queued") { stateLabel = "Kuyrukta…"; }
  else if (job.status === "processing") { stateLabel = "Gönderiliyor…"; }

  return (
    <div ref={jobRef} className={`mt-6 card-soft overflow-hidden border-2 ${stateBg} scroll-mt-24`} data-testid="job-progress">
      <div className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="tiny-label">Gönderim Durumu</div>
            <div className="font-display font-bold text-2xl text-slate-900 mt-1 flex items-center gap-2">
              {!isDone && <Loader2 className="w-6 h-6 animate-spin text-turq-600" />}
              {isDone && !isFail && !isPartial && <CheckCircle2 className="w-6 h-6 text-emerald-600" />}
              {isFail && <AlertTriangle className="w-6 h-6 text-red-600" />}
              {isPartial && <AlertTriangle className="w-6 h-6 text-amber-600" />}
              {stateLabel}
            </div>
            <div className="text-xs text-slate-500 mt-1 font-mono">İş: {(job.job_id || job.id || "").slice(0, 8)}…</div>
          </div>
          <div className="text-right">
            <div className={`font-display font-black text-4xl bg-gradient-to-br ${stateColor} bg-clip-text text-transparent`}>{pct}%</div>
            <div className="text-xs text-slate-500">{processed.toLocaleString("tr-TR")} / {total.toLocaleString("tr-TR")}</div>
          </div>
        </div>

        <div className="w-full bg-white/70 h-2.5 rounded-full overflow-hidden border border-white">
          <div className={`bg-gradient-to-r ${stateColor} h-2.5 transition-all duration-500`} style={{ width: `${pct}%` }} />
        </div>

        <div className="mt-4 grid grid-cols-3 gap-4 text-sm">
          <div>
            <div className="tiny-label">Toplam</div>
            <div className="font-mono font-bold text-slate-900 text-lg">{total.toLocaleString("tr-TR")}</div>
          </div>
          <div>
            <div className="tiny-label">Başarılı</div>
            <div className="font-mono font-bold text-emerald-600 text-lg">{(job.success_count || 0).toLocaleString("tr-TR")}</div>
          </div>
          <div>
            <div className="tiny-label">Hatalı</div>
            <div className="font-mono font-bold text-red-600 text-lg">{(job.fail_count || 0).toLocaleString("tr-TR")}</div>
          </div>
        </div>
      </div>

      {gw && (
        <div className="px-6 pb-6">
          <div className="border-t border-slate-200 pt-4">
            <div className="tiny-label mb-2 flex items-center gap-1.5">
              <MessageCircle className="w-3 h-3" />
              Sistem Yanıtı
            </div>
            <pre data-testid="gateway-response" className={`text-xs font-mono p-3 rounded-lg border whitespace-pre-wrap break-all ${isFail || isPartial ? "bg-red-50 text-red-800 border-red-200" : "bg-emerald-50 text-emerald-800 border-emerald-200"}`}>
              {gw}
            </pre>
          </div>
        </div>
      )}

      {isDone && (
        <div className="px-6 pb-6">
          <button
            onClick={onNew}
            data-testid="new-send-btn"
            className="w-full sm:w-auto btn-primary text-sm font-semibold px-6 py-2.5 rounded-xl flex items-center justify-center gap-2"
          >
            <Send className="w-4 h-4" /> Yeni Gönderim Yap
          </button>
        </div>
      )}
    </div>
  );
}

function PortalShell({ info, children, loggedIn = false, onLogout, portalUser = "" }) {
  return (
    <div className="min-h-screen dot-bg">
      <header className="bg-white/70 backdrop-blur-md border-b border-turq-100 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <BrandMark variant="icon" className="w-9 h-9" />
            <div>
              <div className="font-display font-black text-base text-slate-900 leading-none">SMS GÖNDERİM</div>
              <div className="tiny-label mt-1">E-Telekom Portal</div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            {info && (
              <div className="text-right hidden sm:block">
                <div className="tiny-label">Müşteri</div>
                <div className="font-semibold text-sm text-slate-900">{info.name}</div>
                {portalUser && (
                  <div className="text-[10px] text-slate-500 font-mono">@{portalUser}</div>
                )}
              </div>
            )}
            {onLogout && (
              <button
                onClick={onLogout}
                data-testid="portal-logout"
                className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-slate-700 border border-slate-200 rounded-lg hover:bg-rose-50 hover:border-rose-200 hover:text-rose-700 transition shrink-0"
              >
                <LogOut className="w-3.5 h-3.5" /> Çıkış
              </button>
            )}
          </div>
        </div>
      </header>
      <main className="max-w-5xl mx-auto px-6 py-6">
        {info && (
          <div className="mb-5 flex items-center gap-3 flex-wrap">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-turq-200">
              <Sparkles className="w-3 h-3 text-turq-600" />
              <span className="tiny-label">Toplu SMS</span>
            </div>
            <p className="text-sm text-slate-600">3 gönderim modu · dosya yükleme · parametreli şablon · zamanlanmış kuyruk · maksimum 200.000 alıcı.</p>
          </div>
        )}
        {children}
      </main>
      <footer className="max-w-5xl mx-auto px-6 py-8 text-[11px] text-slate-500 border-t border-turq-100 mt-12">
        {loggedIn || portalUser
          ? "Güvenli oturum · oturumunuz tarayıcınızda saklanır. İşiniz bittiğinde çıkış yapın."
          : "Bu sayfa size özel oluşturulmuş tokenli bir bağlantıdır. Lütfen başkalarıyla paylaşmayın."}
      </footer>
    </div>
  );
}
